import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Download, Wifi, WifiOff, Cloud, RefreshCw, CheckCircle, AlertCircle, Info, Settings, Search, SortAsc, SortDesc, Grid, List, Trash2, FileText, FileText as FileTextIcon, Cloud as CloudIcon } from 'lucide-react';

interface OfflineArticle {
  id: string;
  title: string;
  content: string;
  author: string;
  publishedAt: Date;
  readTime: number;
  category: string;
  tags: string[];
  summary: string;
  keyPoints: string[];
  insights: string[];
  imageUrl?: string;
  audioUrl?: string;
  videoUrl?: string;
  downloadSize: number; // in bytes
  downloadDate: Date;
  lastAccessed: Date;
  isDownloaded: boolean;
  isAvailableOffline: boolean;
  syncStatus: 'synced' | 'pending' | 'error' | 'conflict';
  version: number;
  checksum: string;
  metadata: {
    wordCount: number;
    readingLevel: 'beginner' | 'intermediate' | 'advanced';
    language: string;
    format: 'html' | 'markdown' | 'pdf' | 'epub';
    quality: 'low' | 'medium' | 'high';
  };
}

interface OfflineReadingProps {
  userId: string;
  onArticleDownload?: (article: OfflineArticle) => void;
  onArticleDelete?: (articleId: string) => void;
  onSyncRequest?: (articleId: string) => void;
  onStorageInfo?: (info: { used: number; total: number; available: number }) => void;
}

const OfflineReading: React.FC<OfflineReadingProps> = ({
  userId,
  onArticleDownload,
  onArticleDelete,
  onSyncRequest,
  onStorageInfo
}) => {
  const [offlineArticles, setOfflineArticles] = useState<OfflineArticle[]>([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [storageInfo, setStorageInfo] = useState({ used: 0, total: 0, available: 0 });
  const [selectedArticles, setSelectedArticles] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'title' | 'downloadDate' | 'lastAccessed' | 'size'>('downloadDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [autoDownload, setAutoDownload] = useState(false);
  const [maxStorage, setMaxStorage] = useState(1024 * 1024 * 1024); // 1GB default
  const [downloadQuality, setDownloadQuality] = useState<'low' | 'medium' | 'high'>('medium');

  const storageRef = useRef<Storage | null>(null);

  useEffect(() => {
    // Check if we're in a browser environment
    if (typeof window !== 'undefined') {
      storageRef.current = localStorage;
      loadOfflineArticles();
      checkStorageInfo();
    }

    // Listen for online/offline events
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (isOnline) {
      syncPendingArticles();
    }
  }, [isOnline]);

  const loadOfflineArticles = async () => {
    try {
      setIsLoading(true);
      // In a real app, this would load from IndexedDB or localStorage
      const sampleArticles: OfflineArticle[] = [
        {
          id: '1',
          title: 'The Future of Web Development',
          content: 'Web development is evolving rapidly with new technologies...',
          author: 'John Doe',
          publishedAt: new Date('2024-01-01'),
          readTime: 8,
          category: 'Technology',
          tags: ['web', 'development', 'future'],
          summary: 'Exploring the latest trends in web development',
          keyPoints: ['New frameworks', 'Performance optimization', 'User experience'],
          insights: ['Mobile-first approach', 'Accessibility matters'],
          imageUrl: 'https://via.placeholder.com/800x400',
          downloadSize: 2048576, // 2MB
          downloadDate: new Date('2024-01-15'),
          lastAccessed: new Date('2024-01-20'),
          isDownloaded: true,
          isAvailableOffline: true,
          syncStatus: 'synced',
          version: 1,
          checksum: 'abc123def456',
          metadata: {
            wordCount: 1200,
            readingLevel: 'intermediate',
            language: 'en',
            format: 'html',
            quality: 'high'
          }
        },
        {
          id: '2',
          title: 'Understanding Machine Learning',
          content: 'Machine learning is transforming industries...',
          author: 'Jane Smith',
          publishedAt: new Date('2024-01-05'),
          readTime: 12,
          category: 'AI',
          tags: ['machine learning', 'ai', 'data science'],
          summary: 'A comprehensive guide to machine learning concepts',
          keyPoints: ['Algorithms', 'Data preprocessing', 'Model evaluation'],
          insights: ['Real-world applications', 'Ethical considerations'],
          downloadSize: 3072000, // 3MB
          downloadDate: new Date('2024-01-10'),
          lastAccessed: new Date('2024-01-18'),
          isDownloaded: true,
          isAvailableOffline: true,
          syncStatus: 'pending',
          version: 2,
          checksum: 'def456ghi789',
          metadata: {
            wordCount: 1800,
            readingLevel: 'advanced',
            language: 'en',
            format: 'html',
            quality: 'high'
          }
        },
        {
          id: '3',
          title: 'Sustainable Living Tips',
          content: 'Simple ways to reduce your environmental impact...',
          author: 'Eco Warrior',
          publishedAt: new Date('2024-01-08'),
          readTime: 6,
          category: 'Lifestyle',
          tags: ['sustainability', 'environment', 'lifestyle'],
          summary: 'Practical tips for sustainable living',
          keyPoints: ['Reduce waste', 'Energy efficiency', 'Sustainable choices'],
          insights: ['Small changes matter', 'Community impact'],
          downloadSize: 1536000, // 1.5MB
          downloadDate: new Date('2024-01-12'),
          lastAccessed: new Date('2024-01-16'),
          isDownloaded: true,
          isAvailableOffline: true,
          syncStatus: 'synced',
          version: 1,
          checksum: 'ghi789jkl012',
          metadata: {
            wordCount: 900,
            readingLevel: 'beginner',
            language: 'en',
            format: 'html',
            quality: 'medium'
          }
        }
      ];
      setOfflineArticles(sampleArticles);
    } catch (err) {
      setError('Failed to load offline articles');
    } finally {
      setIsLoading(false);
    }
  };

  const checkStorageInfo = () => {
    if (storageRef.current) {
      try {
        let used = 0;
        for (let key in storageRef.current) {
          if (storageRef.current.hasOwnProperty(key)) {
            used += storageRef.current[key].length;
          }
        }
        
        // Estimate total storage (browsers typically allow 5-10MB for localStorage)
        const total = 10 * 1024 * 1024; // 10MB
        const available = total - used;
        
        setStorageInfo({ used, total, available });
        onStorageInfo?.({ used, total, available });
      } catch (err) {
        console.error('Failed to check storage info:', err);
      }
    }
  };

  const syncPendingArticles = async () => {
    const pendingArticles = offlineArticles.filter(article => article.syncStatus === 'pending');
    
    for (const article of pendingArticles) {
      try {
        // Simulate sync process
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        setOfflineArticles(prev => 
          prev.map(a => 
            a.id === article.id 
              ? { ...a, syncStatus: 'synced' as const, version: a.version + 1 }
              : a
          )
        );
        
        onSyncRequest?.(article.id);
      } catch (err) {
        setOfflineArticles(prev => 
          prev.map(a => 
            a.id === article.id 
              ? { ...a, syncStatus: 'error' as const }
              : a
          )
        );
      }
    }
  };

  const handleDownloadArticle = async (article: OfflineArticle) => {
    try {
      setIsLoading(true);
      
      // Simulate download process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const downloadedArticle = {
        ...article,
        isDownloaded: true,
        isAvailableOffline: true,
        downloadDate: new Date(),
        lastAccessed: new Date(),
        syncStatus: 'synced' as const
      };
      
      setOfflineArticles(prev => [...prev, downloadedArticle]);
      onArticleDownload?.(downloadedArticle);
      
      // Update storage info
      checkStorageInfo();
    } catch (err) {
      setError('Failed to download article');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteArticle = (articleId: string) => {
    setOfflineArticles(prev => prev.filter(article => article.id !== articleId));
    onArticleDelete?.(articleId);
    checkStorageInfo();
  };

  const handleBulkDelete = () => {
    selectedArticles.forEach(articleId => {
      handleDeleteArticle(articleId);
    });
    setSelectedArticles(new Set());
  };

  const handleSelectArticle = (articleId: string) => {
    setSelectedArticles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(articleId)) {
        newSet.delete(articleId);
      } else {
        newSet.add(articleId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedArticles.size === filteredArticles.length) {
      setSelectedArticles(new Set());
    } else {
      setSelectedArticles(new Set(filteredArticles.map(article => article.id)));
    }
  };

  const formatFileSize = (bytes: number): string => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getSyncStatusIcon = (status: string) => {
    switch (status) {
      case 'synced': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'pending': return <RefreshCw className="h-4 w-4 text-yellow-500 animate-spin" />;
      case 'error': return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'conflict': return <AlertCircle className="h-4 w-4 text-orange-500" />;
      default: return <Info className="h-4 w-4 text-gray-500" />;
    }
  };

  const getSyncStatusText = (status: string) => {
    switch (status) {
      case 'synced': return 'Synced';
      case 'pending': return 'Syncing...';
      case 'error': return 'Sync Error';
      case 'conflict': return 'Conflict';
      default: return 'Unknown';
    }
  };

  const filteredArticles = offlineArticles
    .filter(article => {
      const matchesStatus = filterStatus === 'all' || article.syncStatus === filterStatus;
      const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           article.author.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           article.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      
      return matchesStatus && matchesSearch;
    })
    .sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortBy) {
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'downloadDate':
          aValue = a.downloadDate.getTime();
          bValue = b.downloadDate.getTime();
          break;
        case 'lastAccessed':
          aValue = a.lastAccessed.getTime();
          bValue = b.lastAccessed.getTime();
          break;
        case 'size':
          aValue = a.downloadSize;
          bValue = b.downloadSize;
          break;
        default:
          return 0;
      }
      
      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

  if (isLoading && offlineArticles.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8">
        <div className="text-red-500 mb-4">{error}</div>
        <button 
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Offline Reading
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Download articles for offline reading and sync across devices
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
              isOnline 
                ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300' 
                : 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300'
            }`}>
              {isOnline ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
              <span className="text-sm font-medium">
                {isOnline ? 'Online' : 'Offline'}
              </span>
            </div>
            
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              <Settings className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Storage Info */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Storage Usage
          </h2>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {formatFileSize(storageInfo.used)} / {formatFileSize(storageInfo.total)}
          </div>
        </div>
        
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2">
          <div
            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(storageInfo.used / storageInfo.total) * 100}%` }}
          />
        </div>
        
        <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
          <span>Used: {formatFileSize(storageInfo.used)}</span>
          <span>Available: {formatFileSize(storageInfo.available)}</span>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search offline articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="synced">Synced</option>
              <option value="pending">Pending</option>
              <option value="error">Error</option>
              <option value="conflict">Conflict</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="downloadDate">Download Date</option>
              <option value="lastAccessed">Last Accessed</option>
              <option value="title">Title</option>
              <option value="size">Size</option>
            </select>

            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              {sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />}
            </button>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex border border-gray-300 dark:border-gray-600 rounded-lg">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
              >
                <Grid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
              >
                <List className="h-4 w-4" />
              </button>
            </div>

            {selectedArticles.size > 0 && (
              <button
                onClick={handleBulkDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete ({selectedArticles.size})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Articles Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredArticles.map((article) => (
            <div
              key={article.id}
              className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedArticles.has(article.id)}
                    onChange={() => handleSelectArticle(article.id)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="flex items-center gap-2">
                    {getSyncStatusIcon(article.syncStatus)}
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {getSyncStatusText(article.syncStatus)}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                    {article.metadata.format.toUpperCase()}
                  </span>
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400">
                    {article.metadata.quality}
                  </span>
                </div>
              </div>

              {article.imageUrl && (
                <div className="mb-4">
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    className="w-full h-32 object-cover rounded-lg"
                  />
                </div>
              )}

              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {article.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {article.summary}
              </p>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                  <span>Author: {article.author}</span>
                  <span>{article.readTime} min read</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                  <span>Downloaded: {formatDate(article.downloadDate)}</span>
                  <span>{formatFileSize(article.downloadSize)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                  <span>Last accessed: {formatDate(article.lastAccessed)}</span>
                  <span>v{article.version}</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {article.tags.map(tag => `#${tag}`).join(' ')}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleDeleteArticle(article.id)}
                    className="p-2 text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredArticles.map((article) => (
            <div
              key={article.id}
              className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <input
                    type="checkbox"
                    checked={selectedArticles.has(article.id)}
                    onChange={() => handleSelectArticle(article.id)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="flex items-center gap-2">
                    {getSyncStatusIcon(article.syncStatus)}
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {getSyncStatusText(article.syncStatus)}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {article.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {article.summary}
                    </p>
                  </div>
                </div>

                <div className="text-right">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                      {article.metadata.format.toUpperCase()}
                    </span>
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400">
                      {article.metadata.quality}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {formatFileSize(article.downloadSize)} • {article.readTime} min read
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-500">
                    Downloaded: {formatDate(article.downloadDate)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {filteredArticles.length === 0 && (
        <div className="text-center py-12">
          <Download className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No offline articles found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Download articles to read them offline
          </p>
          <button
            onClick={() => {/* Navigate to article list */}}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Browse Articles
          </button>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Offline Settings
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Auto-download new articles
                </label>
                <input
                  type="checkbox"
                  checked={autoDownload}
                  onChange={(e) => setAutoDownload(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Download Quality
                </label>
                <select
                  value={downloadQuality}
                  onChange={(e) => setDownloadQuality(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="low">Low (faster download)</option>
                  <option value="medium">Medium (balanced)</option>
                  <option value="high">High (best quality)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Max Storage: {formatFileSize(maxStorage)}
                </label>
                <input
                  type="range"
                  min="100000000"
                  max="5000000000"
                  step="100000000"
                  value={maxStorage}
                  onChange={(e) => setMaxStorage(parseInt(e.target.value))}
                  className="w-full"
                />
              </div>
            </div>

            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={() => setShowSettings(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfflineReading;
